import React, { useState } from "react";
import ConfirmUnclaimDialog from "@components/ConfirmUnclaimDialog/ConfirmUnclaimDialog";
import UnclaimButton from "@components/FlowActionButtons/UnclaimButton";
import { useNotification } from "@context/NotificationContext";
import { unclaimGatepassById } from "@services/gatepassDataApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";
import type { GatepassFlowData } from "./ClaimGatepass";
import { canUserUnclaim } from "./utils";

interface UnclaimGatepassProps {
  gatepassFlowData?: GatepassFlowData;
}

const LANG_BASE = "gatepass";

const UnclaimGatepass: React.FC<UnclaimGatepassProps> = ({ gatepassFlowData }) => {
  const service = "bff-gatepass";
  const { notify } = useNotification();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const unclaimGatepassMutation = useMutation({
    mutationFn: (id: string) =>
      runTransaction(
        {
          module: "gatepass",
          screen: "GatepassDetails",
          action: "unclaim_gatepass",
          entityType: "gatepass",
        },
        (transactionId) =>
          unclaimGatepassById({
            service,
            id,
            ...(transactionId ? { transactionId } : {}),
          }),
      ),
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: ["gatepass-request-preview", gatepassFlowData?.id],
      });
      notify({
        title: t("gatepass.unclaim-success", { defaultValue: "Gatepass unclaimed" }),
        type: "success",
      });
      await queryClient.invalidateQueries({ queryKey: ["gatepasses"] });
    },
    onError: (error: Error) =>
      notify({
        title: t("common.error"),
        description: error?.message ?? t("common.something-went-wrong"),
        type: "error",
      }),
  });

  if (!gatepassFlowData?.task) {
    return null;
  }

  const isActionAvailable = Boolean(
    canUserUnclaim(
      gatepassFlowData.task.status,
      gatepassFlowData.task.claimOwnerName,
      gatepassFlowData.status,
      gatepassFlowData.task.role,
    ),
  );

  const handleUnclaimClick = () => {
    unclaimGatepassMutation.mutate(gatepassFlowData.id);
  };

  return (
    <>
      <UnclaimButton
        langBase={LANG_BASE}
        permission="canClaimGatepass"
        isActionAvailable={isActionAvailable}
        onUnclaim={() => setShowConfirmDialog(true)}
      />
      <ConfirmUnclaimDialog
        isOpen={showConfirmDialog}
        setIsOpen={setShowConfirmDialog}
        languageBase="gatepass"
        itemId={gatepassFlowData?.serialNumber ?? gatepassFlowData?.id ?? ""}
        confirmAction={handleUnclaimClick}
        testId="unclaim-gatepass-confirm-dialog"
      />
    </>
  );
};

export default UnclaimGatepass;
